import { LoadingOverlay } from "@dehub/theme";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthApi, GeneralApi } from "./apis";
import { AccessPage } from "./features/access";
import { RegisterPage } from "./features/auth";
import { BusinessPage } from "./features/business";
import { DashboardPage } from "./features/dashboard";
import { NotFoundPage } from "./features/not-found";
import { PartnerPage } from "./features/partner";
import { BranchPage } from "./features/branch";
import { WarehouseDetailPage, WarehousePage } from "./features/warehouse";
import { WarehouseStaffPermissionsProvider } from "./features/warehouse-staff";
import { useFirebaseNotifications } from "./firebase/use-firebase-notifications";
import type { IAuth } from "./models/Auth";
import { Layout } from "./shared/layout";
import { authMe } from "./store/Auth";
import { initGeneral } from "./store/General";
import { message } from "./utils/message";

let __WINDOW_LOADED__ = false;

function App() {
  const [loading, setLoading] = React.useState(false);
  const dispatch = useDispatch();

  const { accessToken } = useSelector((state: { auth: IAuth }) => state.auth);
  useFirebaseNotifications(accessToken);

  const init = React.useCallback(async () => {
    setLoading(true);
    (async () => {
      try {
        if (accessToken) {
          const res = await GeneralApi.init();

          dispatch(initGeneral(res));

          const auth = await AuthApi.me();
          dispatch(authMe(auth));
        }
        setLoading(false);
      } catch (error: any) {
        message.error(error?.message);
        setLoading(false);
      }
    })();
    setLoading(false);
  }, [accessToken, dispatch]);

  React.useEffect(() => {
    if (!__WINDOW_LOADED__) init();

    __WINDOW_LOADED__ = true;
  }, [init]);

  if (loading) return <LoadingOverlay visible={loading} />;

  return (
    <WarehouseStaffPermissionsProvider>
      <Routes>
        <Route path="/register" element={<RegisterPage />} />
        {/* /access bootstraps auth from the URL token — must render outside
            Layout so Layout's Header doesn't fire protected API calls with
            the stale persisted token and bounce us back to auth-admin on 401. */}
        <Route path="/access" element={<AccessPage />} />
        <Route path="/" element={<Layout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/organization" element={<PartnerPage />} />
          <Route path="/business" element={<BusinessPage />} />
          {/* old bookmarks and links keep working */}
          <Route path="/our-supplier" element={<Navigate replace to="/business?type=supplier" />} />
          <Route path="/our-buyer" element={<Navigate replace to="/business?type=buyer" />} />
          <Route path="/our-sectors" element={<BranchPage />} />
          <Route path="/our-warehouse" element={<WarehousePage />} />
          <Route path="/our-warehouse/:id" element={<WarehouseDetailPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </WarehouseStaffPermissionsProvider>
  );
}

export default App;
